import React, { useState, useRef, useEffect } from "react";
import { User, LayoutDashboard, Settings, LogOut, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from '../../../../../context/AuthContext';
import { env } from "../../../../../config/env";

const AdminProfileMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Construction de l'URL complète de la photo
  const getProfilePhotoUrl = (path: string | null | undefined): string | undefined => {
    if (!path) return undefined;
    if (path.startsWith("http")) return path;
    return `${env.IMAGE_BASE_URL}${path.replace(/^\//, "")}`;
  };

  // Fermer le menu si clic extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
    navigate("/auth/login");
  };

  // Initiales pour l'avatar par défaut
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase() || "A";
  };

  // Liens du dashboard admin
  const adminLinks = [
    { icon: <LayoutDashboard size={14} />, label: "Tableau de bord", path: "/admin/dashboard/home" },
    { icon: <User size={14} />, label: "Mon profil", path: "/admin/dashboard/profile" },
    { icon: <Settings size={14} />, label: "Paramètres", path: "/admin/dashboard/settings" },
  ];

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 transition-all duration-200 focus:outline-none"
        aria-label="Menu administrateur"
      >
        <div className="relative flex items-center justify-center w-9 h-9 rounded-xl bg-black/20 text-white border border-white/30 backdrop-blur-sm hover:bg-accent hover:text-black hover:scale-105 transition-all duration-200 shadow-md">
          {user?.profile_photo_path ? (
            <img
              src={getProfilePhotoUrl(user.profile_photo_path)}
              alt="Avatar"
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <span className="text-sm font-semibold">
              {user ? getInitials(user.first_name, user.last_name) : "A"}
            </span>
          )}
        </div>
        <ChevronDown
          size={14}
          className={`text-white/80 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-56 rounded-xl bg-black/90 backdrop-blur-md border border-accent/30 shadow-2xl overflow-hidden z-50"
          >
            {user && (
              <div className="p-3 border-b border-white/10">
                <p className="text-white font-medium text-sm truncate">
                  {user.first_name} {user.last_name}
                </p>
                <p className="text-white/50 text-xs truncate">{user.email}</p>
              </div>
            )}
            <div className="py-1">
              {adminLinks.map((link, idx) => (
                <Link
                  key={idx}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-white/80 hover:bg-accent/20 hover:text-white transition-colors"
                >
                  {link.icon}
                  {link.label}
                </Link>
              ))}
              <hr className="my-1 border-white/10" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-white/80 hover:bg-red-500/20 hover:text-red-400 transition-colors"
              >
                <LogOut size={14} className="text-red-400" />
                Déconnexion
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminProfileMenu;